import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  LineChart, Line, PieChart, Pie, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts';
import { BarChart3, TrendingUp, MessageSquare, Download, FileText, ArrowUp, ArrowDown, Minus, Send, Wrench } from 'lucide-react';
import { MdError, MdWarning, MdCheckCircle, MdInfo, MdNotificationsActive } from 'react-icons/md';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Cores conforme identidade visual Lets
const COLORS = {
  promoters: '#10b981',
  passives: '#f59e0b',
  detractors: '#ef4444',
  nps: '#FF8C00',
  csat: '#f5576c',
  ces: '#00f2fe'
};

// Metas padrão (fallback) — valores reais vêm de /api/goals
const DEFAULT_GOALS = {
  nps: 8.0,     // NPS Média (0-10) - Meta: >= nps_goal
  csat: 4.5,    // CSAT Média (1-5) - Meta: >= csat_goal
  ces: 5.0      // CES Média (1-7) - Meta: <= ces_goal (quanto menor, melhor)
};

// Perguntas das pesquisas conforme documento do projeto
const SURVEY_QUESTIONS = {
  nps: 'Em uma escala de 0 a 10, o quanto voce recomendaria a Lets a um amigo?',
  csat: 'Em uma escala de 1 a 5, o quanto voce ficou satisfeito(a) com o atendimento?',
  ces: 'Em uma escala de 1 a 7, onde 1 e muito facil e 7 e muito dificil, quanto esforco voce precisou fazer para resolver sua solicitacao?'
};

// Função para determinar performance e retornar ícone/cor
const getPerformanceIndicator = (metric, value, goals = DEFAULT_GOALS) => {
  const numValue = parseFloat(value);

  if (metric === 'nps') {
    // Usando média NPS (0-10)
    if (numValue >= goals.nps) {
      return { icon: <ArrowUp size={20} />, color: '#10b981', status: 'acima' };
    } else if (numValue >= goals.nps * 0.75) {
      return { icon: <Minus size={20} />, color: '#f59e0b', status: 'atencao' };
    } else {
      return { icon: <ArrowDown size={20} />, color: '#ef4444', status: 'abaixo' };
    }
  } else if (metric === 'csat') {
    if (numValue >= goals.csat) {
      return { icon: <ArrowUp size={20} />, color: '#10b981', status: 'acima' };
    } else if (numValue >= goals.csat * 0.78) {
      return { icon: <Minus size={20} />, color: '#f59e0b', status: 'atencao' };
    } else {
      return { icon: <ArrowDown size={20} />, color: '#ef4444', status: 'abaixo' };
    }
  } else if (metric === 'ces') {
    // CES: menor é melhor (1-7, onde 1 é muito fácil)
    if (numValue <= goals.ces) {
      return { icon: <ArrowUp size={20} />, color: '#10b981', status: 'acima' };
    } else if (numValue <= goals.ces + (7 - goals.ces) * 0.5) {
      return { icon: <Minus size={20} />, color: '#f59e0b', status: 'atencao' };
    } else {
      return { icon: <ArrowDown size={20} />, color: '#ef4444', status: 'abaixo' };
    }
  }
  return { icon: <Minus size={20} />, color: '#6b7280', status: 'neutro' };
};

const normalizeText = (str) =>
  (str || '')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const MULTI_FILTERS = [
  ['sector', 'sector'],
  ['channel', 'channel'],
  ['motive', 'motive'],
  ['classification', 'classification'],
  ['subclassification', 'subclassification'],
  ['client', 'client'],
  ['agent', 'agent'],
  ['branch', 'branch'],
];

const buildFilterParams = (filters) => {
  const params = new URLSearchParams();
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  MULTI_FILTERS.forEach(([stateKey, paramKey]) => {
    const arr = filters[stateKey];
    if (Array.isArray(arr) && arr.length > 0) {
      params.append(paramKey, arr.join(','));
    }
  });
  if (filters.metric) params.append('metric', filters.metric);
  return params;
};

const namesFromIds = (ids, options) => {
  if (!Array.isArray(ids) || ids.length === 0) return [];
  const byId = new Map(options.map(o => [String(o.id), o.name]));
  return ids.map(id => byId.get(String(id)) || id);
};

function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Todos',
  emptyLabel = 'Todos',
  multiple = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const selectedSet = useMemo(() => {
    if (multiple) return new Set((value || []).map(String));
    return new Set(value ? [String(value)] : []);
  }, [value, multiple]);

  const listOptions = useMemo(
    () => (multiple ? options : [{ id: '', name: emptyLabel }, ...options]),
    [options, emptyLabel, multiple]
  );

  const filtered = useMemo(() => {
    const q = normalizeText(query);
    if (!q) return listOptions;
    return listOptions.filter(o => normalizeText(o.name).includes(q));
  }, [listOptions, query]);

  const controlLabel = useMemo(() => {
    if (multiple) {
      const arr = value || [];
      if (arr.length === 0) return placeholder;
      const byId = new Map(options.map(o => [String(o.id), o.name]));
      const names = arr.map(id => byId.get(String(id))).filter(Boolean);
      if (names.length <= 2) return names.join(', ');
      return `${names.length} selecionados`;
    }
    const found = options.find(o => String(o.id) === String(value));
    if (found) return found.name;
    return value ? placeholder : emptyLabel;
  }, [options, value, multiple, placeholder, emptyLabel]);

  const isEmpty = multiple ? (!value || value.length === 0) : !value;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(0);
  }, [filtered, highlight]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const handleSelect = (opt) => {
    if (multiple) {
      const arr = value || [];
      const id = String(opt.id);
      const exists = arr.some(v => String(v) === id);
      const next = exists ? arr.filter(v => String(v) !== id) : [...arr, opt.id];
      onChange(next);
    } else {
      onChange(opt.id);
      setOpen(false);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(multiple ? [] : '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) handleSelect(filtered[highlight]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className={`searchable-select ${open ? 'is-open' : ''} ${multiple ? 'is-multiple' : ''}`} ref={wrapperRef}>
      <button
        type="button"
        className="searchable-select__control"
        onClick={() => setOpen(o => !o)}
      >
        <span className={`searchable-select__value ${isEmpty ? 'is-empty' : ''}`}>
          {controlLabel}
        </span>
        <span className="searchable-select__actions">
          {!isEmpty && (
            <span
              className="searchable-select__clear"
              role="button"
              aria-label="Limpar seleção"
              onMouseDown={handleClear}
              title="Limpar"
            >
              ×
            </span>
          )}
          <span className="searchable-select__caret" aria-hidden>▾</span>
        </span>
      </button>
      {open && (
        <div className="searchable-select__menu">
          <input
            ref={searchRef}
            type="text"
            className="searchable-select__search"
            placeholder="Buscar..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
            onKeyDown={handleKeyDown}
          />
          <ul className="searchable-select__list" ref={listRef}>
            {filtered.length === 0 && (
              <li className="searchable-select__empty">Nenhum resultado</li>
            )}
            {filtered.map((opt, idx) => {
              const selected = selectedSet.has(String(opt.id));
              return (
                <li
                  key={`${opt.id}-${idx}`}
                  data-idx={idx}
                  className={`searchable-select__option ${idx === highlight ? 'is-highlighted' : ''} ${selected ? 'is-selected' : ''}`}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
                >
                  {multiple && (
                    <span className={`searchable-select__checkbox ${selected ? 'is-checked' : ''}`} aria-hidden>
                      {selected ? '✓' : ''}
                    </span>
                  )}
                  <span className="searchable-select__option-label">{opt.name}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pesquisa de Manutenção — campanha da tabela `survey`, pergunta única (0-10)
// ---------------------------------------------------------------------------

const fmtInt = (n) => Number(n || 0).toLocaleString('pt-BR');

const fmtDecimal = (n) =>
  n === null || n === undefined
    ? '—'
    : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const fmtPct = (n) => `${fmtDecimal(n ?? 0)}%`;

// Faixas da nota de manutenção (mesma leitura do NPS: 9-10 ótimo, 7-8 neutro)
const maintenanceTone = (value) => {
  if (value === null || value === undefined) return 'empty';
  const n = parseFloat(value);
  if (Number.isNaN(n)) return 'empty';
  if (n >= 9) return 'good';
  if (n >= 7) return 'warn';
  return 'bad';
};

function MaintenanceBreakdown({ rows, emptyLabel }) {
  if (!rows || rows.length === 0) {
    return <div className="mnt-empty">{emptyLabel}</div>;
  }

  const maxSent = Math.max(...rows.map((r) => r.sent), 1);
  const maxRate = Math.max(...rows.map((r) => r.adherence), 1);

  return (
    <div className="mnt-rows">
      {rows.map((row) => (
        <div className="mnt-row" key={row.label}>
          <div className="mnt-row-head">
            <span className="mnt-row-label" title={row.label}>{row.label}</span>
            <span className={`mnt-row-score tone-${maintenanceTone(row.average)}`}>
              {fmtDecimal(row.average)}
            </span>
          </div>
          <div className="mnt-track" title={`${fmtInt(row.sent)} disparos`}>
            <div className="mnt-fill volume" style={{ width: `${(row.sent / maxSent) * 100}%` }} />
          </div>
          <div className="mnt-track slim" title={`${fmtPct(row.adherence)} de adesão`}>
            <div className="mnt-fill rate" style={{ width: `${(row.adherence / maxRate) * 100}%` }} />
          </div>
          <div className="mnt-row-meta">
            <span>{fmtInt(row.sent)} disparos</span>
            <span className="mnt-dot">•</span>
            <span>{fmtInt(row.responses)} respostas</span>
            <span className="mnt-dot">•</span>
            <span className="mnt-row-rate">{fmtPct(row.adherence)} adesão</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Faixas da nota, na mesma leitura do NPS. A barra é proporcional ao total de
// respostas; o rótulo interno só aparece quando o segmento comporta o texto.
const MAINTENANCE_BANDS = [
  { key: 'promoters', tone: 'good', label: 'Promotores', range: '9-10' },
  { key: 'passives', tone: 'warn', label: 'Neutros', range: '7-8' },
  { key: 'detractors', tone: 'bad', label: 'Detratores', range: '0-6' },
];

// Escapa um valor para CSV com separador ';'
const escapeCSV = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(';') || str.includes('\n') || str.includes('"')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// Monta o arquivo e dispara o download no navegador
const downloadCSV = (rows, filename) => {
  const csv = rows.map((row) => row.join(';')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.setAttribute('href', URL.createObjectURL(blob));
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

function MaintenanceSection({ data, campaign, onCampaignChange, onExport, exporting }) {
  if (!data || !data.hasData) return null;

  const totalCampaignSends = data.campaigns.reduce((sum, item) => sum + item.total, 0);

  const bands = MAINTENANCE_BANDS.map((band) => {
    const count = data[band.key] || 0;
    return {
      ...band,
      count,
      percentage: data.responses > 0 ? (count / data.responses) * 100 : 0,
    };
  });

  return (
    <section className="mnt-section">
      <div className="mnt-header">
        <div className="mnt-heading">
          <div className="mnt-icon"><Wrench size={22} /></div>
          <h2 className="mnt-title">Pesquisa de Manutenção</h2>
        </div>
        <button
          type="button"
          className="mnt-export"
          onClick={onExport}
          disabled={exporting}
          title="Exporta a base da campanha com os filtros aplicados"
        >
          <Download size={15} />
          {exporting ? 'Exportando...' : 'Exportar base'}
        </button>
      </div>

      {/* Segmentação por campanha */}
      {data.campaigns.length > 0 && (
        <div className="mnt-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={campaign === ''}
            className={`mnt-tab ${campaign === '' ? 'active' : ''}`}
            onClick={() => onCampaignChange('')}
          >
            Todas
            <span className="mnt-tab-count">{fmtInt(totalCampaignSends)}</span>
          </button>
          {data.campaigns.map((item) => (
            <button
              type="button"
              role="tab"
              key={item.name}
              aria-selected={campaign === item.name}
              className={`mnt-tab ${campaign === item.name ? 'active' : ''}`}
              onClick={() => onCampaignChange(item.name)}
              title={item.name}
            >
              <span className="mnt-tab-label">{item.name}</span>
              <span className="mnt-tab-count">{fmtInt(item.total)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="mnt-kpis">
        <div className="mnt-kpi">
          <div className="mnt-kpi-icon send"><Send size={16} /></div>
          <div className="mnt-kpi-value">{fmtInt(data.sent)}</div>
          <div className="mnt-kpi-label">Disparos realizados</div>
        </div>

        <div className="mnt-kpi">
          <div className="mnt-kpi-icon answer"><MessageSquare size={16} /></div>
          <div className="mnt-kpi-value">{fmtInt(data.responses)}</div>
          <div className="mnt-kpi-label">Respostas recebidas</div>
          {data.withComment > 0 && (
            <div className="mnt-kpi-hint">{fmtInt(data.withComment)} com comentário</div>
          )}
        </div>

        <div className="mnt-kpi">
          <div className="mnt-kpi-icon rate"><TrendingUp size={16} /></div>
          <div className="mnt-kpi-value accent">{fmtPct(data.adherence)}</div>
          <div className="mnt-kpi-label">Taxa de adesão</div>
          {data.withoutScore > 0 && (
            <div className="mnt-kpi-hint warn">
              {fmtInt(data.withoutScore)} iniciaram sem concluir
            </div>
          )}
        </div>

        <div className="mnt-kpi wide">
          <div className="mnt-score-area">
            <div className="mnt-score-main">
              <div className={`mnt-score-value tone-${maintenanceTone(data.average)}`}>
                {fmtDecimal(data.average)}
              </div>
              <div className="mnt-kpi-label">Nota média (0-10)</div>
            </div>
            {data.responses > 0 ? (
              <div className="mnt-bands">
                <div className="mnt-band-bar">
                  {bands
                    .filter((band) => band.count > 0)
                    .map((band) => (
                      <div
                        key={band.key}
                        className={`mnt-band tone-${band.tone}`}
                        style={{ width: `${band.percentage}%` }}
                        title={`${band.label} (${band.range}): ${fmtInt(band.count)} de ${fmtInt(data.responses)}`}
                      >
                        {/* Abaixo de 14% o rótulo não cabe sem cortar */}
                        {band.percentage >= 14 && (
                          <span className="mnt-band-value">{Math.round(band.percentage)}%</span>
                        )}
                      </div>
                    ))}
                </div>
                <div className="mnt-band-legend">
                  {bands.map((band) => (
                    <span className="mnt-band-item" key={band.key}>
                      <i className={`mnt-band-dot tone-${band.tone}`} />
                      <span className="mnt-band-label">{band.label}</span>
                      <strong>{fmtInt(band.count)}</strong>
                      <em>({band.range})</em>
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mnt-empty">Sem respostas no período</div>
            )}
          </div>
        </div>
      </div>

      <div className="mnt-frames">
        <div className="mnt-frame">
          <div className="mnt-frame-title">Canais de atendimento</div>
          <div className="mnt-frame-subtitle">Volume disparado e adesão por canal</div>
          <MaintenanceBreakdown rows={data.byChannel} emptyLabel="Nenhum canal no período" />
        </div>
        <div className="mnt-frame">
          <div className="mnt-frame-title">Desempenho por setor</div>
          <div className="mnt-frame-subtitle">Volume disparado e adesão por setor de Manutenção</div>
          <MaintenanceBreakdown rows={data.bySector} emptyLabel="Nenhum setor no período" />
        </div>
      </div>

      {data.comments.length > 0 && (
        <div className="mnt-feedback">
          <div className="mnt-frame-title">O que disseram</div>
          <div className="mnt-frame-subtitle">
            {fmtInt(data.comments.length)} feedback{data.comments.length === 1 ? '' : 's'} escrito
            {data.comments.length === 1 ? '' : 's'} · das piores notas para as melhores
          </div>
          {/* Mesmo padrão visual da seção de Comentários, sem o filtro de notas:
              a campanha tem pergunta única, não há métrica para alternar. */}
          <div className="comments-list mnt-feedback-list">
            {data.comments.map((item) => (
              <div className="comment-card" key={item.id}>
                <div className="comment-header">
                  <div className="comment-info">
                    <span className="comment-protocol-link comment-protocol-static">
                      {item.contract || '—'}
                    </span>
                    <div className="comment-meta">
                      {item.client && `${item.client} • `}
                      {item.sector && `${item.sector} • `}
                      {item.channel && `${item.channel} • `}
                      {/* Data/hora em que a resposta chegou; cai para a do
                          disparo apenas se a resposta não tiver sido datada. */}
                      {item.answeredAt || item.sentAt}
                    </div>
                  </div>
                  <div className="comment-scores">
                    <div className={`comment-score tone-${maintenanceTone(item.score)}`}>
                      Nota: {item.score}
                    </div>
                  </div>
                </div>
                <div className="comment-text">{item.comment}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Calcula período padrão: últimos 30 dias
  const getDefaultDates = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };

  const defaultDates = getDefaultDates();

  const [filters, setFilters] = useState({
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    sector: [],
    channel: [],
    motive: [],
    classification: [],
    subclassification: [],
    client: [],
    agent: [],
    branch: [],
    metric: ''
  });

  // Filter options
  const [sectors, setSectors] = useState([]);
  const [channels, setChannels] = useState([]);
  const [motives, setMotives] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [subclassifications, setSubclassifications] = useState([]);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [branches, setBranches] = useState([]);

  const [overview, setOverview] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [trend, setTrend] = useState([]);
  const [distribution, setDistribution] = useState({ nps: [], csat: [], ces: [] });
  const [comments, setComments] = useState([]);
  const [commentsFilter, setCommentsFilter] = useState('all');
  const [cesBySector, setCesBySector] = useState([]);
  const [cesByMotive, setCesByMotive] = useState([]);
  const [rankingNps, setRankingNps] = useState([]);
  const [rankingCsat, setRankingCsat] = useState([]);
  const [rankingCes, setRankingCes] = useState([]);
  const [goals, setGoals] = useState(DEFAULT_GOALS);
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceCampaign, setMaintenanceCampaign] = useState('');
  const [exportingMaintenance, setExportingMaintenance] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
    fetchGoals();
    // eslint-disable-next-line
  }, []);

  const fetchGoals = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/goals`);
      setGoals({
        nps: parseFloat(res.data.nps) || DEFAULT_GOALS.nps,
        csat: parseFloat(res.data.csat) || DEFAULT_GOALS.csat,
        ces: parseFloat(res.data.ces) || DEFAULT_GOALS.ces,
      });
    } catch (err) {
      console.error('Erro ao buscar metas — usando valores padrão:', err);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [filters]);

  // Bloco da pesquisa de Manutenção: recarrega sozinho, tanto pelos filtros do
  // topo quanto pela pesquisa escolhida no seletor do próprio bloco.
  useEffect(() => {
    const fetchMaintenance = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/metrics/maintenance-survey?${buildMaintenanceParams()}`);
        setMaintenance(res.data);
      } catch (err) {
        console.error('Erro ao buscar pesquisa de manutenção:', err);
        setMaintenance(null);
      }
    };

    fetchMaintenance();
    // eslint-disable-next-line
  }, [filters, maintenanceCampaign]);

  const fetchFilterOptions = async () => {
    try {
      const [sectorsRes, channelsRes, motivesRes, classificationsRes, subclassificationsRes, clientsRes, agentsRes, branchesRes] = await Promise.all([
        axios.get(`${API_URL}/api/filters/sectors`),
        axios.get(`${API_URL}/api/filters/channels`),
        axios.get(`${API_URL}/api/filters/motives`),
        axios.get(`${API_URL}/api/filters/classifications`),
        axios.get(`${API_URL}/api/filters/subclassifications`),
        axios.get(`${API_URL}/api/filters/clients`),
        axios.get(`${API_URL}/api/filters/agents`),
        axios.get(`${API_URL}/api/filters/branches`)
      ]);

      setSectors(sectorsRes.data);
      setChannels(channelsRes.data);
      setMotives(motivesRes.data);
      setClassifications(classificationsRes.data);
      setSubclassifications(subclassificationsRes.data);
      setClients(clientsRes.data);
      setAgents(agentsRes.data);
      setBranches(branchesRes.data);
    } catch (err) {
      console.error('Erro ao buscar opções de filtros:', err);
    }
  };

  // Cascata cliente -> filial: recarrega filiais quando cliente muda
  // e remove filiais selecionadas que não pertençam mais aos clientes escolhidos.
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const params = new URLSearchParams();
        if (filters.client.length > 0) params.append('clients', filters.client.join(','));
        const res = await axios.get(`${API_URL}/api/filters/branches?${params}`);
        setBranches(res.data);

        const validIds = new Set(res.data.map(b => String(b.id)));
        setFilters(prev => {
          const cleaned = prev.branch.filter(id => validIds.has(String(id)));
          if (cleaned.length === prev.branch.length) return prev;
          return { ...prev, branch: cleaned };
        });
      } catch (err) {
        console.error('Erro ao buscar filiais:', err);
      }
    };
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.client]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = buildFilterParams(filters);

      // Buscar dados principais
      const overviewRes = await axios.get(`${API_URL}/api/metrics/overview?${params}`);
      setOverview(overviewRes.data);

      const alertsRes = await axios.get(`${API_URL}/api/alerts?${params}`);
      setAlerts(alertsRes.data);

      const trendRes = await axios.get(`${API_URL}/api/metrics/trend?${params}`);
      setTrend(trendRes.data);

      // Buscar distribuições
      try {
        const [npsDistRes, csatDistRes, cesDistRes] = await Promise.all([
          axios.get(`${API_URL}/api/metrics/distribution?metric=nps&${params}`),
          axios.get(`${API_URL}/api/metrics/distribution?metric=csat&${params}`),
          axios.get(`${API_URL}/api/metrics/distribution?metric=ces&${params}`)
        ]);

        setDistribution({
          nps: npsDistRes.data,
          csat: csatDistRes.data,
          ces: cesDistRes.data
        });
      } catch (distErr) {
        console.error('Erro ao buscar distribuições:', distErr);
      }

      // Buscar CES por setor e por motivo
      try {
        const [cesBySectorRes, cesByMotiveRes] = await Promise.all([
          axios.get(`${API_URL}/api/metrics/by-sector?${params}`),
          axios.get(`${API_URL}/api/metrics/by-motive?${params}`)
        ]);

        setCesBySector(cesBySectorRes.data);
        setCesByMotive(cesByMotiveRes.data);
      } catch (cesErr) {
        console.error('Erro ao buscar CES por setor/motivo:', cesErr);
      }

      // Buscar comentários (opcional)
      try {
        const commentsRes = await axios.get(`${API_URL}/api/comments?limit=50&${params}`);
        setComments(commentsRes.data);
      } catch (commErr) {
        console.error('Erro ao buscar comentários:', commErr);
        setComments([]);
      }

      // Buscar rankings
      try {
        const [npsRankRes, csatRankRes, cesRankRes] = await Promise.all([
          axios.get(`${API_URL}/api/rankings/nps?${params}`),
          axios.get(`${API_URL}/api/rankings/csat?${params}`),
          axios.get(`${API_URL}/api/rankings/ces?${params}`)
        ]);

        setRankingNps(npsRankRes.data);
        setRankingCsat(csatRankRes.data);
        setRankingCes(cesRankRes.data);
      } catch (rankErr) {
        console.error('Erro ao buscar rankings:', rankErr);
        setRankingNps([]);
        setRankingCsat([]);
        setRankingCes([]);
      }

    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados do dashboard. Verifique se o backend está rodando.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const getAlertIcon = (type) => {
    const iconProps = { size: 24 };

    switch (type) {
      case 'error':
        return <MdError {...iconProps} />;
      case 'warning':
        return <MdWarning {...iconProps} />;
      case 'success':
        return <MdCheckCircle {...iconProps} />;
      default:
        return <MdInfo {...iconProps} />;
    }
  };

  const prepareNPSPieData = () => {
    if (!overview) return [];
    return [
      { name: 'Promotores', value: overview.nps.promoters, color: COLORS.promoters },
      { name: 'Passivos', value: overview.nps.passives, color: COLORS.passives },
      { name: 'Detratores', value: overview.nps.detractors, color: COLORS.detractors }
    ];
  };

  const prepareCSATBarData = () => {
    return distribution.csat.map(item => ({
      name: `${item.score} Estrela${item.score > 1 ? 's' : ''}`,
      value: parseInt(item.count)
    }));
  };

  const filteredComments = comments.filter(comment => {
    if (commentsFilter === 'all') return true;
    if (commentsFilter === 'nps') return comment.nps.score !== null;
    if (commentsFilter === 'csat') return comment.csat.score !== null;
    if (commentsFilter === 'ces') return comment.ces.score !== null;
    return true;
  });

  // Exportar para CSV
  // Filtros que a campanha de Manutenção reconhece — os mesmos usados na busca
  // do bloco, para que a exportação traga exatamente o recorte que está na tela.
  const buildMaintenanceParams = () => {
    const params = buildFilterParams({
      startDate: filters.startDate,
      endDate: filters.endDate,
      sector: filters.sector,
      channel: filters.channel,
      client: filters.client,
      agent: filters.agent
    });
    if (maintenanceCampaign) params.append('campaign', maintenanceCampaign);
    return params;
  };

  // Exporta a base da campanha de Manutenção (disparos respondidos e não respondidos)
  const exportMaintenanceCSV = async () => {
    setExportingMaintenance(true);
    try {
      const params = buildMaintenanceParams();
      const response = await axios.get(`${API_URL}/api/metrics/maintenance-survey/export?${params}`);
      const rows = response.data;

      if (!rows || rows.length === 0) {
        alert('Não há dados da pesquisa de manutenção para exportar com os filtros aplicados.');
        return;
      }

      const data = [];
      data.push(['BASE DA PESQUISA DE MANUTENÇÃO - LETS FROTAS']);
      data.push([`Data de Geração: ${new Date().toLocaleString('pt-BR')}`]);
      data.push(['']);

      data.push(['FILTROS APLICADOS']);
      data.push([`Período: ${new Date(filters.startDate).toLocaleDateString('pt-BR')} a ${new Date(filters.endDate).toLocaleDateString('pt-BR')}`]);
      const pushIfNames = (label, arr, opts) => {
        const names = namesFromIds(arr, opts);
        if (names.length > 0) data.push([escapeCSV(`${label}: ${names.join(', ')}`)]);
      };
      pushIfNames('Setor', filters.sector, sectors);
      pushIfNames('Canal', filters.channel, channels);
      pushIfNames('Cliente', filters.client, clients);
      pushIfNames('Consultor', filters.agent, agents);
      data.push([escapeCSV(`Pesquisa: ${maintenanceCampaign || 'Todas as pesquisas'}`)]);
      data.push(['']);

      if (maintenance) {
        data.push(['RESUMO']);
        data.push(['Indicador', 'Valor', 'Observação']);
        data.push(['Disparos realizados', maintenance.sent, '-']);
        data.push(['Respostas recebidas', maintenance.responses, `${maintenance.withComment} com comentário`]);
        // Decimal com vírgula: com ponto o Excel pt-BR lê como texto
        data.push(['Taxa de adesão', `${String(maintenance.adherence).replace('.', ',')}%`, 'Respostas / disparos']);
        data.push(['Nota média', String(maintenance.average ?? '-').replace('.', ','), 'Escala 0-10']);
        data.push(['Promotores / Neutros / Detratores', `${maintenance.promoters} / ${maintenance.passives} / ${maintenance.detractors}`, '9-10 / 7-8 / 0-6']);
        data.push(['']);
      }

      data.push(['BASE DE DISPAROS']);
      data.push([
        'Pesquisa', 'Contrato', 'Cliente', 'Contato', 'E-mail', 'Telefone',
        'Setor', 'Filial', 'Consultor', 'Canal',
        '1º Envio (E-mail)', '2º Envio (WhatsApp)', '3º Envio (E-mail)', '4º Envio (WhatsApp)',
        'Participou', 'Nota', 'Data/Hora da Resposta', 'Comentário'
      ]);

      rows.forEach((row) => {
        data.push([
          escapeCSV(row.campaign || '-'),
          escapeCSV(row.contract || '-'),
          escapeCSV(row.client || '-'),
          escapeCSV(row.contact || '-'),
          escapeCSV(row.email || '-'),
          escapeCSV(row.phone || '-'),
          escapeCSV(row.sector || '-'),
          escapeCSV(row.branch || '-'),
          escapeCSV(row.consultant || '-'),
          escapeCSV(row.channel || '-'),
          escapeCSV(row.firstSend || '-'),
          escapeCSV(row.secondSend || '-'),
          escapeCSV(row.thirdSend || '-'),
          escapeCSV(row.fourthSend || '-'),
          escapeCSV(row.participated ? 'Sim' : 'Não'),
          escapeCSV(row.score !== null && row.score !== undefined ? row.score : '-'),
          escapeCSV(row.answeredAt || '-'),
          escapeCSV(row.comment || '')
        ]);
      });

      data.push(['']);
      data.push([`Total de registros exportados: ${rows.length}`]);

      downloadCSV(data, `pesquisa-manutencao-lets-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      console.error('Erro ao exportar pesquisa de manutenção:', err);
      alert('Erro ao exportar a base da pesquisa de manutenção. Tente novamente.');
    } finally {
      setExportingMaintenance(false);
    }
  };

  const exportToCSV = async () => {
    // Buscar TODOS os comentários para exportação (sem limite)
    let allComments = [];
    try {
      const params = buildFilterParams(filters);

      // Buscar todos os registros (limit=10000 para garantir todos)
      const response = await axios.get(`${API_URL}/api/comments?limit=10000&${params}`);
      allComments = response.data;
    } catch (err) {
      console.error('Erro ao buscar dados para exportação:', err);
      alert('Erro ao buscar dados para exportação. Tente novamente.');
      return;
    }

    if (!allComments || allComments.length === 0) {
      alert('Não há dados de pesquisas para exportar.');
      return;
    }

    const data = [];

    // ==================== INFORMAÇÕES DO RELATÓRIO ====================
    data.push(['RELATÓRIO DE PESQUISAS DE SATISFAÇÃO - LETS FROTAS']);
    data.push([`Data de Geração: ${new Date().toLocaleString('pt-BR')}`]);
    data.push(['']);

    // ==================== FILTROS APLICADOS ====================
    data.push(['FILTROS APLICADOS']);

    // Período sempre é exibido
    data.push([`Período: ${new Date(filters.startDate).toLocaleDateString('pt-BR')} a ${new Date(filters.endDate).toLocaleDateString('pt-BR')}`]);

    // Outros filtros
    const pushIfNames = (label, arr, opts) => {
      const names = namesFromIds(arr, opts);
      if (names.length > 0) data.push([`${label}: ${names.join(', ')}`]);
    };
    pushIfNames('Setor', filters.sector, sectors);
    pushIfNames('Canal', filters.channel, channels);
    pushIfNames('Motivo', filters.motive, motives);
    pushIfNames('Classificação', filters.classification, classifications);
    pushIfNames('Subclassificação', filters.subclassification, subclassifications);
    pushIfNames('Cliente', filters.client, clients);
    pushIfNames('Filial', filters.branch, branches);
    pushIfNames('Consultor', filters.agent, agents);
    if (filters.metric) {
      const metricNames = { nps: 'NPS', csat: 'CSAT', ces: 'CES' };
      data.push([`Métrica: ${metricNames[filters.metric] || filters.metric}`]);
    }
    data.push(['']);

    // ==================== RESUMO DAS MÉTRICAS ====================
    if (overview) {
      data.push(['RESUMO DAS MÉTRICAS']);
      data.push(['Métrica', 'Valor', 'Meta', 'Observação']);

      if (overview.responseRate) {
        data.push(['Taxa de Resposta', `${overview.responseRate.percentage}%`, '-', `${overview.responseRate.totalResponses} respostas de ${overview.responseRate.totalInvites} convites`]);
      }

      data.push(['Média NPS', overview.nps.average || '-', goals.nps, 'Escala 1-10']);
      data.push(['Score NPS', overview.nps.score || '-', '-', `Promotores: ${overview.nps.promoters} | Neutros: ${overview.nps.passives} | Detratores: ${overview.nps.detractors}`]);
      data.push(['Média CSAT', overview.csat.score || '-', goals.csat, `${overview.csat.percentage}% satisfeitos (${overview.csat.satisfied} de ${overview.csat.total})`]);
      data.push(['Média CES', overview.ces.score || '-', `≤ ${goals.ces}`, `${overview.ces.percentage}% baixo esforço (${overview.ces.lowEffort} de ${overview.ces.total})`]);
      data.push(['']);
    }

    // ==================== DADOS DETALHADOS DAS PESQUISAS ====================
    data.push(['DADOS DETALHADOS DAS PESQUISAS']);
    data.push([
      'Protocolo',
      'Data',
      'Cliente',
      'Filial',
      'Setor',
      'Canal',
      'Motivo',
      'Consultor',
      'Nota NPS',
      'Comentário NPS',
      'Nota CSAT',
      'Comentário CSAT',
      'Nota CES',
      'Comentário CES'
    ]);

    // Adicionar cada pesquisa
    allComments.forEach(comment => {
      data.push([
        escapeCSV(comment.protocol || '-'),
        escapeCSV(comment.createdAt ? new Date(comment.createdAt).toLocaleDateString('pt-BR') : '-'),
        escapeCSV(comment.client || '-'),
        escapeCSV(comment.branch || '-'),
        escapeCSV(comment.sector || '-'),
        escapeCSV(comment.channel || '-'),
        escapeCSV(comment.motive || '-'),
        escapeCSV(comment.consultant || '-'),
        escapeCSV(comment.nps?.score !== null ? comment.nps.score : '-'),
        escapeCSV(comment.nps?.comment || ''),
        escapeCSV(comment.csat?.score !== null ? comment.csat.score : '-'),
        escapeCSV(comment.csat?.comment || ''),
        escapeCSV(comment.ces?.score !== null ? comment.ces.score : '-'),
        escapeCSV(comment.ces?.comment || '')
      ]);
    });

    data.push(['']);
    data.push([`Total de registros exportados: ${allComments.length}`]);

    // Converter para CSV com separador ;
    const csvContent = data.map(row => row.join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `pesquisas-satisfacao-lets-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Exportar para PDF
  const exportToPDF = async () => {
    if (!overview) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Função auxiliar para adicionar cabeçalho em cada página
    const addHeader = async (doc, isFirstPage = false) => {
      // Tentar carregar e adicionar logo
      try {
        const logoUrl = 'https://cdn.prod.website-files.com/67d07156353c8ca5e0ed7af5/67d086c965688b051f7e1e5e_logo-lets-frotas-gestao.png';
        const img = new Image();
        img.crossOrigin = 'Anonymous';

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = logoUrl;
        });

        // Adicionar logo (altura fixa de 12, largura proporcional)
        const imgHeight = 12;
        const imgWidth = (img.width / img.height) * imgHeight;
        doc.addImage(img, 'PNG', 14, 10, imgWidth, imgHeight);
      } catch (e) {
        // Se falhar ao carregar logo, usar texto
        doc.setFontSize(16);
        doc.setTextColor(255, 140, 0);
        doc.text('LETS FROTAS', 14, 18);
      }

      // Titulo do relatorio
      doc.setFontSize(16);
      doc.setTextColor(51, 51, 51);
      doc.text('Relatorio de Satisfacao do Cliente', pageWidth - 14, 15, { align: 'right' });

      // Data de geracao
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, pageWidth - 14, 22, { align: 'right' });

      // Linha separadora
      doc.setDrawColor(255, 140, 0);
      doc.setLineWidth(0.5);
      doc.line(14, 28, pageWidth - 14, 28);

      return 35; // Retorna posição Y após o cabeçalho
    };

    // Função para adicionar rodapé
    const addFooter = (doc, pageNum) => {
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Pagina ${pageNum}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      doc.text('Lets Frotas - Gestao de Frotas', 14, pageHeight - 10);
      doc.text('Documento confidencial', pageWidth - 14, pageHeight - 10, { align: 'right' });
    };

    let pageNum = 1;
    let yPos = await addHeader(doc, true);

    // ==================== RESUMO EXECUTIVO ====================
    doc.setFontSize(14);
    doc.setTextColor(255, 140, 0);
    doc.text('Resumo Executivo', 14, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setTextColor(80);
    const resumoTexto = 'Este relatorio apresenta os indicadores de satisfacao dos clientes da Lets Frotas, medidos atraves das metodologias NPS (Net Promoter Score), CSAT (Customer Satisfaction Score) e CES (Customer Effort Score). O objetivo e monitorar a experiencia do cliente, identificar oportunidades de melhoria e garantir a excelencia no atendimento.';
    const resumoLines = doc.splitTextToSize(resumoTexto, pageWidth - 28);
    doc.text(resumoLines, 14, yPos);
    yPos += resumoLines.length * 5 + 8;

    // ==================== FILTROS APLICADOS ====================
    doc.setFontSize(12);
    doc.setTextColor(255, 140, 0);
    doc.text('Filtros Aplicados', 14, yPos);
    yPos += 6;

    // Período sempre é exibido
    const pdfFiltros = [
      ['Periodo', `${new Date(filters.startDate).toLocaleDateString('pt-BR')} a ${new Date(filters.endDate).toLocaleDateString('pt-BR')}`]
    ];

    // Outros filtros
    const pushPdfIfNames = (label, arr, opts) => {
      const names = namesFromIds(arr, opts);
      if (names.length > 0) pdfFiltros.push([label, names.join(', ')]);
    };
    pushPdfIfNames('Setor', filters.sector, sectors);
    pushPdfIfNames('Canal', filters.channel, channels);
    pushPdfIfNames('Motivo', filters.motive, motives);
    pushPdfIfNames('Classificacao', filters.classification, classifications);
    pushPdfIfNames('Subclassificacao', filters.subclassification, subclassifications);
    pushPdfIfNames('Cliente', filters.client, clients);
    pushPdfIfNames('Filial', filters.branch, branches);
    pushPdfIfNames('Consultor', filters.agent, agents);
    if (filters.metric) {
      const metricNames = { nps: 'NPS', csat: 'CSAT', ces: 'CES' };
      pdfFiltros.push(['Metrica', metricNames[filters.metric] || filters.metric]);
    }

    autoTable(doc, {
      startY: yPos,
      head: [['Filtro', 'Valor']],
      body: pdfFiltros,
      theme: 'plain',
      headStyles: { fillColor: [240, 240, 240], textColor: [51, 51, 51], fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        1: { cellWidth: 'auto' }
      }
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // ==================== KPIs DE SUCESSO ====================
    doc.setFontSize(12);
    doc.setTextColor(255, 140, 0);
    doc.text('KPIs de Sucesso - Metas do Projeto', 14, yPos);
    yPos += 6;

    autoTable(doc, {
      startY: yPos,
      head: [['Indicador', 'Meta', 'Resultado Atual', 'Status']],
      body: [
        ['NPS Medio', `>= ${goals.nps}`, overview.nps.average || '-', parseFloat(overview.nps.average) >= goals.nps ? 'Atingida' : 'Abaixo'],
        ['CSAT Medio', `>= ${goals.csat}`, overview.csat.score || '-', parseFloat(overview.csat.score) >= goals.csat ? 'Atingida' : 'Abaixo'],
        ['CES Medio', `<= ${goals.ces}`, overview.ces.score || '-', parseFloat(overview.ces.score) <= goals.ces ? 'Atingida' : 'Abaixo']
      ],
      theme: 'striped',
      headStyles: { fillColor: [255, 140, 0], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: {
        3: { fontStyle: 'bold' }
      },
      didParseCell: function(data) {
        if (data.column.index === 3 && data.section === 'body') {
          if (data.cell.raw === 'Atingida') {
            data.cell.styles.textColor = [16, 185, 129]; // Verde
          } else if (data.cell.raw === 'Abaixo') {
            data.cell.styles.textColor = [239, 68, 68]; // Vermelho
          }
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // ==================== MÉTRICAS PRINCIPAIS ====================
    doc.setFontSize(14);
    doc.setTextColor(255, 140, 0);
    doc.text('Indicadores de Desempenho Detalhados', 14, yPos);
    yPos += 8;

    // Taxa de Resposta
    if (overview.responseRate) {
      autoTable(doc, {
        startY: yPos,
        head: [['Taxa de Resposta', 'Valor', 'Observacao']],
        body: [
          ['Percentual de Respostas', `${overview.responseRate.percentage}%`, 'Proporcao de clientes que responderam a pesquisa'],
          ['Convites Enviados', overview.responseRate.totalInvites?.toLocaleString('pt-BR') || '0', 'Total de pesquisas enviadas no periodo'],
          ['Respostas Recebidas', overview.responseRate.totalResponses?.toLocaleString('pt-BR') || '0', 'Total de respostas validas coletadas']
        ],
        theme: 'striped',
        headStyles: { fillColor: [255, 140, 0], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: { 2: { fontStyle: 'italic', textColor: [100, 100, 100] } }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }

    // NPS
    doc.setFontSize(12);
    doc.setTextColor(51, 51, 51);
    doc.text('NPS - Net Promoter Score', 14, yPos);
    yPos += 6;

    autoTable(doc, {
      startY: yPos,
      head: [['Indicador', 'Valor', 'Meta', 'Descricao']],
      body: [
        ['Media NPS', overview.nps.average || '-', `>= ${goals.nps}`, 'Media das notas de recomendacao (escala 0-10)'],
        ['Promotores (9-10)', overview.nps.promoters || 0, '-', 'Clientes que recomendam ativamente a empresa'],
        ['Neutros (7-8)', overview.nps.passives || 0, '-', 'Clientes satisfeitos, mas vulneraveis a concorrencia'],
        ['Detratores (0-6)', overview.nps.detractors || 0, '-', 'Clientes insatisfeitos que podem prejudicar a marca'],
        ['Total de Avaliacoes', overview.nps.total || 0, '-', 'Quantidade de respostas NPS no periodo']
      ],
      theme: 'striped',
      headStyles: { fillColor: [255, 140, 0], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: { 3: { fontStyle: 'italic', textColor: [100, 100, 100] } }
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // CSAT
    if (yPos > 200) {
      addFooter(doc, pageNum);
      doc.addPage();
      pageNum++;
      yPos = await addHeader(doc);
    }

    doc.setFontSize(12);
    doc.setTextColor(51, 51, 51);
    doc.text('CSAT - Customer Satisfaction Score', 14, yPos);
    yPos += 6;

    autoTable(doc, {
      startY: yPos,
      head: [['Indicador', 'Valor', 'Meta', 'Descricao']],
      body: [
        ['Media CSAT', overview.csat.score || '-', `>= ${goals.csat}`, 'Media de satisfacao (escala 1-5 estrelas)'],
        ['Clientes Satisfeitos (4-5)', overview.csat.satisfied || 0, '-', 'Avaliacoes 4 ou 5 estrelas'],
        ['Percentual de Satisfacao', `${overview.csat.percentage || 0}%`, '-', 'Proporcao de clientes satisfeitos'],
        ['Total de Avaliacoes', overview.csat.total || 0, '-', 'Quantidade de respostas CSAT no periodo']
      ],
      theme: 'striped',
      headStyles: { fillColor: [255, 140, 0], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: { 3: { fontStyle: 'italic', textColor: [100, 100, 100] } }
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // CES
    doc.setFontSize(12);
    doc.setTextColor(51, 51, 51);
    doc.text('CES - Customer Effort Score', 14, yPos);
    yPos += 6;

    autoTable(doc, {
      startY: yPos,
      head: [['Indicador', 'Valor', 'Meta', 'Descricao']],
      body: [
        ['Media CES', overview.ces.score || '-', `<= ${goals.ces}`, 'Media de esforco (escala 1-7, quanto menor melhor)'],
        ['Baixo Esforco (1-3)', overview.ces.lowEffort || 0, '-', 'Clientes que resolveram com facilidade'],
        ['Percentual Baixo Esforco', `${overview.ces.percentage || 0}%`, '-', 'Proporcao de atendimentos com baixo esforco'],
        ['Total de Avaliacoes', overview.ces.total || 0, '-', 'Quantidade de respostas CES no periodo']
      ],
      theme: 'striped',
      headStyles: { fillColor: [255, 140, 0], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: { 3: { fontStyle: 'italic', textColor: [100, 100, 100] } }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // ==================== RANKINGS ====================
    if (yPos > 180) {
      addFooter(doc, pageNum);
      doc.addPage();
      pageNum++;
      yPos = await addHeader(doc);
    }

    doc.setFontSize(14);
    doc.setTextColor(255, 140, 0);
    doc.text('Rankings por Setor', 14, yPos);
    yPos += 10;

    // Ranking NPS
    if (rankingNps.length > 0) {
      doc.setFontSize(11);
      doc.setTextColor(51, 51, 51);
      doc.text('Top 10 - Melhores NPS por Setor', 14, yPos);
      yPos += 6;

      const npsRankingData = rankingNps.slice(0, 10).map(item => [
        item.rank,
        item.sectorName?.substring(0, 35) || '-',
        item.avgNps || '-',
        item.totalResponses || 0
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Posicao', 'Setor', 'Media NPS', 'Respostas']],
        body: npsRankingData,
        theme: 'striped',
        headStyles: { fillColor: [255, 140, 0], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }

    // Ranking CSAT
    if (yPos > 180) {
      addFooter(doc, pageNum);
      doc.addPage();
      pageNum++;
      yPos = await addHeader(doc);
    }

    if (rankingCsat.length > 0) {
      doc.setFontSize(11);
      doc.setTextColor(51, 51, 51);
      doc.text('Top 10 - Melhores CSAT por Setor', 14, yPos);
      yPos += 6;

      const csatRankingData = rankingCsat.slice(0, 10).map(item => [
        item.rank,
        item.sectorName?.substring(0, 35) || '-',
        item.avgCsat || '-',
        `${item.satisfiedPercent || 0}%`,
        item.totalResponses || 0
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Posicao', 'Setor', 'CSAT Medio', '% Satisfeitos', 'Respostas']],
        body: csatRankingData,
        theme: 'striped',
        headStyles: { fillColor: [255, 140, 0], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }

    // Ranking CES
    if (yPos > 180) {
      addFooter(doc, pageNum);
      doc.addPage();
      pageNum++;
      yPos = await addHeader(doc);
    }

    if (rankingCes.length > 0) {
      doc.setFontSize(11);
      doc.setTextColor(51, 51, 51);
      doc.text('Top 10 - Menor Esforco (CES) por Setor', 14, yPos);
      yPos += 6;

      const cesRankingData = rankingCes.slice(0, 10).map(item => [
        item.rank,
        item.sectorName?.substring(0, 35) || '-',
        item.avgCes || '-',
        `${item.lowEffortPercent || 0}%`,
        item.totalResponses || 0
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Posicao', 'Setor', 'CES Medio', '% Baixo Esforco', 'Respostas']],
        body: cesRankingData,
        theme: 'striped',
        headStyles: { fillColor: [255, 140, 0], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2 }
      });
      yPos = doc.lastAutoTable.finalY + 15;
    }

    // ==================== COMENTARIOS DOS CLIENTES ====================
    let commentsForPdf = [];
    try {
      const params = buildFilterParams({ ...filters, metric: '' });

      const resp = await axios.get(`${API_URL}/api/comments?limit=10000&${params}`);
      commentsForPdf = resp.data || [];
    } catch (e) {
      commentsForPdf = comments || [];
    }

    const commentsWithText = commentsForPdf.filter(c =>
      (c.nps && c.nps.comment) || (c.csat && c.csat.comment) || (c.ces && c.ces.comment)
    );

    if (commentsWithText.length > 0) {
      addFooter(doc, pageNum);
      doc.addPage();
      pageNum++;
      yPos = await addHeader(doc);

      doc.setFontSize(14);
      doc.setTextColor(255, 140, 0);
      doc.text('Comentarios dos Clientes', 14, yPos);
      yPos += 6;

      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Total de comentarios com texto: ${commentsWithText.length}`, 14, yPos);
      yPos += 8;

      const commentsBody = commentsWithText.map(c => {
        const partes = [];
        if (c.nps && c.nps.comment) partes.push(`NPS (${c.nps.score}): ${c.nps.comment}`);
        if (c.csat && c.csat.comment) partes.push(`CSAT (${c.csat.score}): ${c.csat.comment}`);
        if (c.ces && c.ces.comment) partes.push(`CES (${c.ces.score}): ${c.ces.comment}`);
        return [
          c.protocol || '-',
          c.createdAt ? new Date(c.createdAt).toLocaleDateString('pt-BR') : '-',
          c.client || '-',
          c.branch || '-',
          c.sector || '-',
          partes.join('\n\n')
        ];
      });

      autoTable(doc, {
        startY: yPos,
        head: [['Protocolo', 'Data', 'Cliente', 'Filial', 'Setor', 'Comentario']],
        body: commentsBody,
        theme: 'striped',
        headStyles: { fillColor: [255, 140, 0], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak', valign: 'top' },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 18 },
          2: { cellWidth: 28 },
          3: { cellWidth: 24 },
          4: { cellWidth: 24 },
          5: { cellWidth: 'auto' }
        },
        didDrawPage: () => {
          const pageCount = doc.internal.getNumberOfPages();
          if (pageCount > pageNum) {
            pageNum = pageCount;
          }
        }
      });
      yPos = doc.lastAutoTable.finalY + 15;
      pageNum = doc.internal.getNumberOfPages();
    }

    // ==================== METODOLOGIA E PERGUNTAS ====================
    if (yPos > 140) {
      addFooter(doc, pageNum);
      doc.addPage();
      pageNum++;
      yPos = await addHeader(doc);
    }

    doc.setFontSize(14);
    doc.setTextColor(255, 140, 0);
    doc.text('Metodologia das Pesquisas', 14, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setTextColor(51, 51, 51);
    doc.text('Perguntas Utilizadas nas Pesquisas:', 14, yPos);
    yPos += 8;

    const perguntasData = [
      ['NPS', SURVEY_QUESTIONS.nps, '0 a 10', 'Mede lealdade e probabilidade de recomendacao'],
      ['CSAT', SURVEY_QUESTIONS.csat, '1 a 5 estrelas', 'Avalia satisfacao com o atendimento'],
      ['CES', SURVEY_QUESTIONS.ces, '1 a 7', 'Mede esforco do cliente (menor = melhor)']
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Metrica', 'Pergunta', 'Escala', 'Objetivo']],
      body: perguntasData,
      theme: 'striped',
      headStyles: { fillColor: [255, 140, 0], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 4 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 18 },
        1: { cellWidth: 80 },
        2: { cellWidth: 25 },
        3: { cellWidth: 'auto', fontStyle: 'italic' }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // ==================== GLOSSÁRIO ====================
    if (yPos > 160) {
      addFooter(doc, pageNum);
      doc.addPage();
      pageNum++;
      yPos = await addHeader(doc);
    }

    doc.setFontSize(14);
    doc.setTextColor(255, 140, 0);
    doc.text('Glossario e Definicoes', 14, yPos);
    yPos += 10;

    const glossario = [
      ['NPS (Net Promoter Score)', 'Metodologia que mede a lealdade do cliente atraves da pergunta de recomendacao. Escala de 0 a 10. Media >= 8,0 indica excelente desempenho.'],
      ['CSAT (Customer Satisfaction)', 'Indice de satisfacao que avalia a experiencia imediata do cliente. Escala de 1 a 5 estrelas. Valores >= 4,5 indicam excelente satisfacao.'],
      ['CES (Customer Effort Score)', 'Mede o esforco necessario para resolver uma demanda. Escala de 1 (muito facil) a 7 (muito dificil). Quanto menor, melhor a experiencia.'],
      ['Promotores', 'Clientes que deram nota 9 ou 10 no NPS. Sao entusiastas leais que continuarao comprando e recomendando.'],
      ['Neutros/Passivos', 'Clientes que deram nota 7 ou 8 no NPS. Estao satisfeitos mas nao entusiasmados, vulneraveis a concorrencia.'],
      ['Detratores', 'Clientes que deram nota de 0 a 6 no NPS. Sao insatisfeitos e podem prejudicar a marca atraves de boca-a-boca negativo.']
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Termo', 'Definicao']],
      body: glossario,
      theme: 'plain',
      headStyles: { fillColor: [240, 240, 240], textColor: [51, 51, 51], fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 4 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 45 },
        1: { cellWidth: 'auto' }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // ==================== BENEFÍCIOS DO PROJETO ====================
    if (yPos > 160) {
      addFooter(doc, pageNum);
      doc.addPage();
      pageNum++;
      yPos = await addHeader(doc);
    }

    doc.setFontSize(14);
    doc.setTextColor(255, 140, 0);
    doc.text('Beneficios do Projeto de Satisfacao', 14, yPos);
    yPos += 10;

    const beneficios = [
      ['Melhoria Continua', 'Identificacao de pontos de melhoria nos processos de atendimento e servicos, permitindo acoes corretivas direcionadas.'],
      ['Retencao de Clientes', 'Acompanhamento da satisfacao permite antecipar problemas e evitar cancelamentos, aumentando a fidelizacao.'],
      ['Fortalecimento da Marca', 'Clientes satisfeitos tornam-se promotores da marca, gerando indicacoes organicas e fortalecendo a reputacao.'],
      ['Tomada de Decisao', 'Dados quantitativos e qualitativos que suportam decisoes estrategicas baseadas na voz do cliente.'],
      ['Benchmarking', 'Metricas padronizadas (NPS, CSAT, CES) permitem comparacao com o mercado e identificacao de gaps.']
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Beneficio', 'Descricao']],
      body: beneficios,
      theme: 'striped',
      headStyles: { fillColor: [255, 140, 0], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 4 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        1: { cellWidth: 'auto' }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // ==================== CONFORMIDADE LGPD ====================
    if (yPos > 180) {
      addFooter(doc, pageNum);
      doc.addPage();
      pageNum++;
      yPos = await addHeader(doc);
    }

    doc.setFontSize(14);
    doc.setTextColor(255, 140, 0);
    doc.text('Conformidade com a LGPD', 14, yPos);
    yPos += 10;

    doc.setFontSize(9);
    doc.setTextColor(51, 51, 51);

    const lgpdTexto = 'Este projeto de pesquisa de satisfacao esta em conformidade com a Lei Geral de Protecao de Dados (LGPD - Lei 13.709/2018). Os dados coletados sao utilizados exclusivamente para fins de melhoria dos servicos e sao tratados com os seguintes principios:';
    const lgpdLines = doc.splitTextToSize(lgpdTexto, pageWidth - 28);
    doc.text(lgpdLines, 14, yPos);
    yPos += lgpdLines.length * 4 + 6;

    const lgpdPrincipios = [
      ['Finalidade', 'Os dados sao coletados exclusivamente para avaliacao e melhoria dos servicos prestados.'],
      ['Adequacao', 'Apenas informacoes necessarias para a pesquisa sao solicitadas.'],
      ['Necessidade', 'Coleta limitada ao minimo necessario para atingir os objetivos.'],
      ['Transparencia', 'O cliente e informado sobre o uso dos dados antes de responder.'],
      ['Seguranca', 'Medidas tecnicas e administrativas protegem os dados contra acessos nao autorizados.'],
      ['Nao Discriminacao', 'Os dados nao sao utilizados para fins discriminatorios.']
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Principio', 'Aplicacao']],
      body: lgpdPrincipios,
      theme: 'plain',
      headStyles: { fillColor: [240, 240, 240], textColor: [51, 51, 51], fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 35 },
        1: { cellWidth: 'auto' }
      }
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // Notas finais
    doc.setFontSize(8);
    doc.setTextColor(100);
    const notasFinais = [
      '- Os dados apresentados neste relatorio sao confidenciais e de uso exclusivo da Lets Frotas.',
      '- As metricas sao calculadas com base nas respostas validas coletadas no periodo selecionado.',
      '- Os dados pessoais dos respondentes sao anonimizados nos relatorios agregados.',
      '- Para duvidas sobre a metodologia ou interpretacao dos dados, entre em contato com a equipe de Qualidade.',
      '- Relatorio gerado automaticamente pelo Sistema de Gestao de Satisfacao - Lets Frotas.'
    ];

    if (yPos > 250) {
      addFooter(doc, pageNum);
      doc.addPage();
      pageNum++;
      yPos = await addHeader(doc);
    }

    notasFinais.forEach((nota, index) => {
      doc.text(nota, 14, yPos + (index * 5));
    });

    // Adicionar rodapé na última página
    addFooter(doc, pageNum);

    // Salvar PDF
    doc.save(`relatorio-satisfacao-lets-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <div className="app">
        <div className="loading">Carregando dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <div className="container">
          <div className="error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="container">
        {/* Filtros */}
        <div className="filters">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Data Início</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Data Fim</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Setor</label>
              <SearchableSelect
                multiple
                value={filters.sector}
                onChange={(v) => handleFilterChange('sector', v)}
                options={sectors}
              />
            </div>
            <div className="filter-group">
              <label>Canal</label>
              <SearchableSelect
                multiple
                value={filters.channel}
                onChange={(v) => handleFilterChange('channel', v)}
                options={channels}
              />
            </div>
            <div className="filter-group">
              <label>Motivo</label>
              <SearchableSelect
                multiple
                value={filters.motive}
                onChange={(v) => handleFilterChange('motive', v)}
                options={motives}
              />
            </div>
            <div className="filter-group">
              <label>Classificação</label>
              <SearchableSelect
                multiple
                value={filters.classification}
                onChange={(v) => handleFilterChange('classification', v)}
                options={classifications}
              />
            </div>
            <div className="filter-group">
              <label>Subclassificação</label>
              <SearchableSelect
                multiple
                value={filters.subclassification}
                onChange={(v) => handleFilterChange('subclassification', v)}
                options={subclassifications}
              />
            </div>
            <div className="filter-group">
              <label>Cliente</label>
              <SearchableSelect
                multiple
                value={filters.client}
                onChange={(v) => handleFilterChange('client', v)}
                options={clients}
              />
            </div>
            <div className="filter-group">
              <label>Filial</label>
              <SearchableSelect
                multiple
                value={filters.branch}
                onChange={(v) => handleFilterChange('branch', v)}
                options={branches}
              />
            </div>
            <div className="filter-group">
              <label>Consultor</label>
              <SearchableSelect
                multiple
                value={filters.agent}
                onChange={(v) => handleFilterChange('agent', v)}
                options={agents}
              />
            </div>
            <div className="filter-group">
              <label>Métrica</label>
              <SearchableSelect
                value={filters.metric}
                onChange={(v) => handleFilterChange('metric', v)}
                options={[
                  { id: 'nps', name: 'NPS' },
                  { id: 'csat', name: 'CSAT' },
                  { id: 'ces', name: 'CES' }
                ]}
                emptyLabel="Todas"
                placeholder="Todas"
              />
            </div>
            <div className="filter-group export-buttons-container">
              <button onClick={exportToCSV} className="export-btn" title="Exportar para CSV">
                <Download size={18} />
                CSV
              </button>
              <button onClick={exportToPDF} className="export-btn" title="Exportar para PDF">
                <FileText size={18} />
                PDF
              </button>
            </div>
          </div>
        </div>

        {/* Cards de Métricas - MOVIDOS PARA O TOPO */}
        {overview && (
          <div className="metrics-grid">
            {/* NPS Card */}
            {(!filters.metric || filters.metric === 'nps') && (
              <div className="metric-card nps">
                <div className="metric-header">
                  <div className="metric-icon nps">
                    <TrendingUp size={24} />
                  </div>
                  <div className="metric-title">NPS</div>
                </div>
                <div className="metric-value-container">
                  <div className="metric-value">{overview.nps.average}</div>
                  <div className="metric-goal-indicator">
                    <div className="metric-goal">Meta: {goals.nps}</div>
                    <div
                      className="performance-indicator"
                      style={{ color: getPerformanceIndicator('nps', overview.nps.average, goals).color }}
                    >
                      {getPerformanceIndicator('nps', overview.nps.average, goals).icon}
                    </div>
                  </div>
                </div>
                <div className="metric-subtitle">Média (1-10)</div>
                <div className="metric-details">
                  <div className="metric-detail">
                    <span className="metric-detail-label">Promotores:</span>
                    <span className="metric-detail-value promoters">{overview.nps.promoters}</span>
                  </div>
                  <div className="metric-detail">
                    <span className="metric-detail-label">Detratores:</span>
                    <span className="metric-detail-value detractors">{overview.nps.detractors}</span>
                  </div>
                  <div className="metric-detail">
                    <span className="metric-detail-label">Passivos:</span>
                    <span className="metric-detail-value passives">{overview.nps.passives}</span>
                  </div>
                  <div className="metric-detail">
                    <span className="metric-detail-label">NPS Score:</span>
                    <span
                      className="metric-detail-value"
                      style={{ color: overview.nps.score > 0 ? '#10b981' : overview.nps.score < 0 ? '#ef4444' : '#f59e0b' }}
                    >
                      {overview.nps.score > 0 ? `+${overview.nps.score}` : overview.nps.score}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* CSAT Card */}
            {(!filters.metric || filters.metric === 'csat') && (
              <div className="metric-card csat">
                <div className="metric-header">
                  <div className="metric-icon csat">
                    <MessageSquare size={24} />
                  </div>
                  <div className="metric-title">CSAT</div>
                </div>
                <div className="metric-value-container">
                  <div className="metric-value">{overview.csat.score}</div>
                  <div className="metric-goal-indicator">
                    <div className="metric-goal">Meta: {goals.csat}</div>
                    <div
                      className="performance-indicator"
                      style={{ color: getPerformanceIndicator('csat', overview.csat.score, goals).color }}
                    >
                      {getPerformanceIndicator('csat', overview.csat.score, goals).icon}
                    </div>
                  </div>
                </div>
                <div className="metric-subtitle">Satisfação Média (1-5)</div>
                <div className="metric-details">
                  <div className="metric-detail">
                    <span className="metric-detail-label">Satisfeitos:</span>
                    <span className="metric-detail-value satisfied">{overview.csat.satisfied}</span>
                  </div>
                  <div className="metric-detail">
                    <span className="metric-detail-label">Total:</span>
                    <span className="metric-detail-value">{overview.csat.total}</span>
                  </div>
                  <div className="metric-detail">
                    <span className="metric-detail-label">Taxa:</span>
                    <span className="metric-detail-value percentage">{overview.csat.percentage}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* CES Card */}
            {(!filters.metric || filters.metric === 'ces') && (
              <div className="metric-card ces">
                <div className="metric-header">
                  <div className="metric-icon ces">
                    <BarChart3 size={24} />
                  </div>
                  <div className="metric-title">CES</div>
                </div>
                <div className="metric-value-container">
                  <div className="metric-value">{overview.ces.score}</div>
                  <div className="metric-goal-indicator">
                    <div className="metric-goal">Meta: ≤ {goals.ces}</div>
                    <div
                      className="performance-indicator"
                      style={{ color: getPerformanceIndicator('ces', overview.ces.score, goals).color }}
                    >
                      {getPerformanceIndicator('ces', overview.ces.score, goals).icon}
                    </div>
                  </div>
                </div>
                <div className="metric-subtitle">Esforço Médio (1-7, 1=muito fácil)</div>
                <div className="metric-details">
                  <div className="metric-detail">
                    <span className="metric-detail-label">Baixo Esforço (1-3):</span>
                    <span className="metric-detail-value low-effort">{overview.ces.lowEffort}</span>
                  </div>
                  <div className="metric-detail">
                    <span className="metric-detail-label">Total:</span>
                    <span className="metric-detail-value">{overview.ces.total}</span>
                  </div>
                  <div className="metric-detail">
                    <span className="metric-detail-label">% Baixo Esforço:</span>
                    <span className="metric-detail-value percentage">{overview.ces.percentage}%</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Alertas e Recomendações */}
        {alerts.length > 0 && (
          <div className="alerts-section">
            <div className="alerts-header">
              <MdNotificationsActive size={20} />
              Alertas e Recomendações
            </div>
            <div className="alerts-container">
              {alerts.map((alert, index) => (
                <div key={index} className={`alert alert-${alert.type}`}>
                  {getAlertIcon(alert.type)}
                  <div className="alert-content">
                    <div className="alert-title">{alert.title}</div>
                    <div className="alert-message">{alert.message}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Taxa de Resposta */}
        {overview && overview.responseRate && (
          <div className="response-rate-section">
            <div className="response-rate-card">
              <div className="response-rate-header">
                <div className="response-rate-icon">
                  <TrendingUp size={28} />
                </div>
                <div className="response-rate-title">Taxa de Resposta</div>
              </div>
              <div className="response-rate-value">{overview.responseRate.percentage}%</div>
              <div className="response-rate-subtitle">Respostas Válidas / Convites Enviados</div>
              <div className="response-rate-details">
                <div className="response-rate-detail">
                  <span className="response-rate-label">Convites Enviados:</span>
                  <span className="response-rate-number">{overview.responseRate.totalInvites.toLocaleString('pt-BR')}</span>
                </div>
                <div className="response-rate-detail">
                  <span className="response-rate-label">Respostas Recebidas:</span>
                  <span className="response-rate-number">{overview.responseRate.totalResponses.toLocaleString('pt-BR')}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gráficos */}
        <div className="charts-grid">
          {/* Distribuição NPS */}
          <div className="chart-card">
            <div className="chart-title">Distribuição NPS</div>
            <div className="chart-subtitle">Comparação de Promotores, Passivos e Detratores</div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={prepareNPSPieData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {prepareNPSPieData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Distribuição CSAT */}
          <div className="chart-card">
            <div className="chart-title">Distribuição CSAT</div>
            <div className="chart-subtitle">Respostas por Nível de Satisfação</div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={prepareCSATBarData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#f5576c" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tendência das Métricas */}
        <div className="chart-section">
          <div className="chart-card">
            <div className="chart-title">Tendência das Métricas</div>
            <div className="chart-subtitle">Evolução nos últimos 6 meses</div>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="nps"
                  stroke={COLORS.nps}
                  strokeWidth={2}
                  name="NPS"
                />
                <Line
                  type="monotone"
                  dataKey="csat"
                  stroke={COLORS.csat}
                  strokeWidth={2}
                  name="CSAT"
                />
                <Line
                  type="monotone"
                  dataKey="ces"
                  stroke={COLORS.ces}
                  strokeWidth={2}
                  name="CES"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráficos CES */}
        <div className="charts-grid">
          {/* CES por Setor */}
          {cesBySector.length > 0 && (
            <div className="chart-card">
              <div className="chart-title">CES por Setor</div>
              <div className="chart-subtitle">Média CES (1-7) por Setor</div>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={cesBySector
                      .filter(item => parseFloat(item.ces) > 0)
                      .map((item) => ({
                        name: item.sector,
                        value: parseFloat(item.ces),
                        total: item.totalCes
                      }))}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={100}
                    fill={COLORS.ces}
                    dataKey="value"
                  >
                    {cesBySector
                      .filter(item => parseFloat(item.ces) > 0)
                      .map((_, index) => (
                        <Cell key={`cell-sector-${index}`} fill={`hsl(${180 + index * 30}, 70%, 50%)`} />
                      ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div style={{
                            background: 'white',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '8px'
                          }}>
                            <p style={{ margin: 0, fontWeight: 'bold' }}>{data.name}</p>
                            <p style={{ margin: '5px 0', color: COLORS.ces }}>
                              CES Médio: {data.value}
                            </p>
                            <p style={{ margin: '5px 0', fontSize: '0.9em', color: '#666' }}>
                              Total: {data.total} avaliações
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* CES por Motivo */}
          {cesByMotive.length > 0 && (
            <div className="chart-card">
              <div className="chart-title">CES por Motivo de Atendimento</div>
              <div className="chart-subtitle">Média CES (1-7) por Motivo</div>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={cesByMotive.map((item, index) => ({
                      name: item.motive,
                      value: parseFloat(item.ces),
                      score: parseFloat(item.ces),
                      total: item.totalCes
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={100}
                    fill={COLORS.ces}
                    dataKey="value"
                  >
                    {cesByMotive.map((entry, index) => (
                      <Cell key={`cell-motive-${index}`} fill={`hsl(${200 + index * 25}, 65%, 55%)`} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div style={{
                            background: 'white',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '8px'
                          }}>
                            <p style={{ margin: 0, fontWeight: 'bold' }}>{data.name}</p>
                            <p style={{ margin: '5px 0', color: COLORS.ces }}>
                              CES Médio: {data.score}
                            </p>
                            <p style={{ margin: '5px 0', fontSize: '0.9em', color: '#666' }}>
                              Total: {data.total} avaliações
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Rankings */}
        <div className="rankings-section">
          <h2 className="section-title">Rankings por Setor</h2>
          <div className="charts-grid-3">

            {/* Ranking NPS */}
            <div className="chart-card">
              <h3 className="chart-title">Top 10 NPS por Setor</h3>
              <p className="chart-subtitle">Setores com melhor NPS Score</p>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={rankingNps.slice(0, 10)}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 10]} />
                  <YAxis
                    dataKey="sectorName"
                    type="category"
                    width={110}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload[0]) {
                        const data = payload[0].payload;
                        return (
                          <div className="custom-tooltip">
                            <p className="tooltip-label">{data.sectorName}</p>
                            <p className="tooltip-value">Média: {data.avgNps}</p>
                            <p className="tooltip-info">Respostas: {data.totalResponses}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="avgNps" fill="#FF8C00" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Ranking CSAT */}
            <div className="chart-card">
              <h3 className="chart-title">Top 10 CSAT por Setor</h3>
              <p className="chart-subtitle">Setores com melhor satisfação</p>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={rankingCsat.slice(0, 10)}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 5]} />
                  <YAxis
                    dataKey="sectorName"
                    type="category"
                    width={110}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload[0]) {
                        const data = payload[0].payload;
                        return (
                          <div className="custom-tooltip">
                            <p className="tooltip-label">{data.sectorName}</p>
                            <p className="tooltip-value">CSAT: {data.avgCsat}</p>
                            <p className="tooltip-info">Respostas: {data.totalResponses}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="avgCsat" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Ranking CES */}
            <div className="chart-card">
              <h3 className="chart-title">Top 10 CES por Setor</h3>
              <p className="chart-subtitle">Setores com menor esforço</p>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={rankingCes.slice(0, 10)}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 7]} />
                  <YAxis
                    dataKey="sectorName"
                    type="category"
                    width={110}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload[0]) {
                        const data = payload[0].payload;
                        return (
                          <div className="custom-tooltip">
                            <p className="tooltip-label">{data.sectorName}</p>
                            <p className="tooltip-value">CES: {data.avgCes}</p>
                            <p className="tooltip-info">Respostas: {data.totalResponses}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="avgCes" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

          </div>
        </div>

        {/* Pesquisa de Manutenção — campanha da tabela `survey`, medida à parte.
            Fica depois dos gráficos para não competir com os indicadores gerais. */}
        <MaintenanceSection
          data={maintenance}
          campaign={maintenanceCampaign}
          onCampaignChange={setMaintenanceCampaign}
          onExport={exportMaintenanceCSV}
          exporting={exportingMaintenance}
        />

        {/* Comentários */}
        <div className="comments-section">
          <div className="comments-header">
            <div className="comments-title">Comentários</div>
            <select
              className="comments-filter"
              value={commentsFilter}
              onChange={(e) => setCommentsFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="nps">NPS</option>
              <option value="csat">CSAT</option>
              <option value="ces">CES</option>
            </select>
          </div>
          <div className="comments-list">
            {filteredComments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                Nenhum comentário encontrado
              </div>
            ) : (
              filteredComments.map((comment) => (
                <div key={comment.id} className="comment-card">
                  <div className="comment-header">
                    <div className="comment-info">
                      {/* Pesquisas importadas não possuem protocolo no CRM — o
                          identificador exibido é o contrato, sem link. */}
                      {comment.source === 'crm' ? (
                        <a
                          href={`https://letscrm.omniflex.com.br/019a0408-09ce-7187-b3e6-813d618108b5/requests?protocol=${comment.protocol}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="comment-protocol-link"
                        >
                          {comment.protocol}
                        </a>
                      ) : (
                        <span className="comment-protocol-link comment-protocol-static">
                          {comment.protocol || '—'}
                        </span>
                      )}
                      <div className="comment-meta">
                        {comment.client && `${comment.client} • `}
                        {comment.branch && (
                          <>
                            <span className="comment-branch-badge">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                <circle cx="12" cy="10" r="3" />
                              </svg>
                              {comment.branch}
                            </span>
                            {' • '}
                          </>
                        )}
                        {comment.sector && `${comment.sector} • `}
                        {new Date(comment.createdAt).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                    <div className="comment-scores">
                      {(commentsFilter === 'all' || commentsFilter === 'nps') && comment.nps.score !== null && (
                        <div className="comment-score nps">
                          NPS: {comment.nps.score}
                        </div>
                      )}
                      {(commentsFilter === 'all' || commentsFilter === 'csat') && comment.csat.score !== null && (
                        <div className="comment-score csat">
                          CSAT: {comment.csat.score}
                        </div>
                      )}
                      {(commentsFilter === 'all' || commentsFilter === 'ces') && comment.ces.score !== null && (
                        <div className="comment-score ces">
                          CES: {comment.ces.score}
                        </div>
                      )}
                    </div>
                  </div>
                  {(commentsFilter === 'all' || commentsFilter === 'nps') && comment.nps.comment && (
                    <div className="comment-text">
                      <strong>NPS:</strong> {comment.nps.comment}
                    </div>
                  )}
                  {(commentsFilter === 'all' || commentsFilter === 'csat') && comment.csat.comment && (
                    <div className="comment-text">
                      <strong>CSAT:</strong> {comment.csat.comment}
                    </div>
                  )}
                  {(commentsFilter === 'all' || commentsFilter === 'ces') && comment.ces.comment && (
                    <div className="comment-text">
                      <strong>CES:</strong> {comment.ces.comment}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
